import type { ReactElement } from 'react';
import { Tooltip } from '@mui/material';
import { styled } from '@mui/material';
import { TaxReliefElegibleType } from 'further-types/exit';

const TooltipWrapper = styled('span')(() => ({
  display: 'inline-block',
  cursor: 'not-allowed',
}));

export type TaxReliefButtonRendererProps = {
  disableEdit: boolean;
  isEntireFieldDisabled: boolean;
  isYesDisabled: boolean;
  isNoDisabled: boolean;
  isPartialDisabled: boolean;
  originalTaxReliefElegible?: TaxReliefElegibleType;
};

/**
 * Renders a tax relief radio button with appropriate tooltip and disabled state.
 * Returns the complete JSX with Tooltip wrapper if needed.
 */
export function renderTaxReliefButton(
  option: TaxReliefElegibleType,
  button: ReactElement,
  props: TaxReliefButtonRendererProps,
): ReactElement {
  const {
    isEntireFieldDisabled,
    isYesDisabled,
    isNoDisabled,
    isPartialDisabled,
  } = props;

  let tooltipText: string | null = null;

  // When entire field is disabled, all options get the same tooltip
  if (isEntireFieldDisabled) {
    tooltipText =
      "This exit's S/EIS eligible status has already been set. If there is no taxable value increase, an edit must follow the original exit's tax status.";
  } else {
    // Per-option tooltips when specific options are disabled
    switch (option) {
      case TaxReliefElegibleType.Yes:
        if (isYesDisabled) {
          tooltipText =
            'This exit was originally not, or partially not, S/EIS qualifying. To change this, please correct the original exit.';
        }
        break;

      case TaxReliefElegibleType.No:
        if (isNoDisabled) {
          tooltipText =
            'This exit was originally partially S/EIS qualifying. You can only select Partial.';
        }
        break;

      case TaxReliefElegibleType.Partial:
        if (isPartialDisabled) {
          tooltipText = 'This exit was originally not S/EIS qualifying.';
        }
        break;
    }
  }

  if (tooltipText) {
    return (
      <Tooltip title={tooltipText} arrow disableInteractive={false}>
        <TooltipWrapper>{button}</TooltipWrapper>
      </Tooltip>
    );
  }

  return button;
}
